import * as Sentry from '@sentry/browser';
import axios from 'axios';
import { SubmissionError } from 'redux-form';
import apiEndpoint from '../../core/legacy/utils/api';

export const getRoadDataIntervalForImageRequest = async (
    imageId: string,
    rubricToDisplayIds: string[],
    referenceToDisplayIds: string[],
    taggedElementsOnly: boolean,
    imageRoaddataOnly,
    signal?: AbortSignal,
) => {
    try {
        const { data } = await axios.request({
            method: 'POST',
            url: `${apiEndpoint}/image/${imageId}/roaddata-interval`,
            data: {
                rubricToDisplayIds,
                referenceToDisplayIds,
                taggedElementsOnly,
                imageRoaddataOnly,
            },
            withCredentials: true,
            signal,
        });

        if (rubricToDisplayIds.length === 0) {
            return {
                roaddatas: [],
                totalSize: data.totalSize,
                rubricIdToCount: data.rubricIdToCount,
            };
        }

        return data;
    } catch (e) {
        // A cancelled request isn't a real failure — a newer navigation superseded it. Let
        // the caller recognize it via axios.isCancel and ignore it silently, instead of
        // logging it as an error and surfacing a user-facing failure message for it.
        if (axios.isCancel(e)) {
            throw e;
        }
        console.error(e);
        Sentry.captureException(e);
        throw new SubmissionError({ _error: 'serverErrors.whileGettingRoadDataInterval' });
    }
};

export type RoadDataFilters = {
    rubricToDisplayIds: string[];
    referenceToDisplayIds: string[];
    taggedElementsOnly: boolean;
    imageRoaddataOnly: boolean;
};

// Combines what used to be two separate requests (annotations + roaddata-interval) into
// one, for the one place that always needs both at once: navigating between images. See
// QueryAnnotationsAndRoadDataForImageUseCase (backend) for why sharing the underlying
// computation is safe. The standalone getRoadDataIntervalForImageRequest above is untouched
// and still used on its own by the mutation flows (delete/add/edit-shape) that only need a
// counts refresh, not annotations.
//
// Takes two separate filter sets rather than one shared set: the annotations view (drawn on
// the image) and the roaddata-interval view (the side panel/list) have never necessarily
// shared the same effective filters — the "apply filter to image" toggle gates the former
// but has always left the latter showing your raw filter selections regardless (see
// UpdateFiltersCommand, which does exactly this for filter-panel-triggered fetches).
export const getAnnotationsAndRoadDataForImageRequest = async (
    imageId: string,
    annotationsFilters: RoadDataFilters,
    intervalFilters: RoadDataFilters,
    signal?: AbortSignal,
) => {
    try {
        const { data } = await axios.request({
            method: 'POST',
            url: `${apiEndpoint}/image/${imageId}/annotations-and-roaddata-interval`,
            data: { annotationsFilters, intervalFilters },
            withCredentials: true,
            signal,
        });

        // Matches the two original requests' behaviour exactly: with no rubric selected
        // for a given view, that view shows nothing, but the counts are still real — each
        // view's own filter set decides its own emptiness independently.
        return {
            annotations: annotationsFilters.rubricToDisplayIds.length === 0 ? [] : data.annotations,
            roaddatas: intervalFilters.rubricToDisplayIds.length === 0 ? [] : data.roaddatas,
            totalSize: data.totalSize,
            rubricIdToCount: data.rubricIdToCount,
        };
    } catch (e) {
        if (axios.isCancel(e)) {
            throw e;
        }
        console.error(e);
        Sentry.captureException(e);
        throw new SubmissionError({ _error: 'serverErrors.whileGettingRoadDataInterval' });
    }
};
